import { useEffect, useRef, useState } from 'react';
import { Sparkles, Search, AlertTriangle, ListTodo, FileText, X, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/components/ui';
import { useTransitionState } from '@/hooks/useTransitionState';

/*
  AISpotlight — modal AI invoker (⌘J).

  Defensive cleanup (Sprint 1.10 fix юзер сообщил «после закрытия UI заблокирован
  бесконечно, помогает только перезагрузка»):

  1. pointer-events: none на backdrop когда `isExiting` — клики проходят сквозь
     overlay сразу как начался exit. Без этого backdrop catch'ил клики пока
     не отыграла exit-анимация (а если что-то залипло — навсегда).
  2. visibility: hidden на корневой контейнер когда phase=closed — даже если
     React почему-то не unmount'ил, контейнер невидим и не ловит клики.
  3. Explicit document focus reset когда phase переходит в closed —
     если input был сфокусирован при закрытии, blur'им чтоб фокус не остался
     на удаляемом элементе.
  4. Backdrop теперь <div> с onClick, не <button> — button может зависнуть
     в focused state на unmount.
*/

interface AISpotlightProps {
  open: boolean;
  onClose: () => void;
}

const SUGGESTIONS = [
  {
    icon: <AlertTriangle size={14} />,
    label: 'Что не так с host-999? Покажи историю падений',
    meta: 'incident.io · host-999 — 4 падения за месяц',
  },
  {
    icon: <Search size={14} />,
    label: 'Где сейчас заканчиваются ресурсы?',
    meta: 'CMDB · capacity по локациям',
  },
  {
    icon: <ListTodo size={14} />,
    label: 'Создай задачу из шаблона "новый сервер"',
    meta: 'Task templates · DC-5 / Площадка Д',
  },
  {
    icon: <FileText size={14} />,
    label: 'Как мы перезагружаем узлы?',
    meta: 'Docs/Wiki · стандартная процедура',
  },
];

interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
  error?: string;
}

export function AISpotlight({ open, onClose }: AISpotlightProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const { phase, shouldRender, isExiting } = useTransitionState(open, {
    enter: 320,
    exit: 200,
  });

  // Sprint S1 (2026-05-24): wired to /api/ai/chat
  const [prompt, setPrompt] = useState('');
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [convoId, setConvoId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  // Auto-scroll к новому turn'у
  useEffect(() => {
    if (turns.length > 0 && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [turns]);

  // Auto-focus input на полном open.
  useEffect(() => {
    if (phase === 'open') {
      const t = window.setTimeout(() => inputRef.current?.focus(), 50);
      return () => window.clearTimeout(t);
    }
  }, [phase]);

  // Reset conversation на полном close (после exit animation)
  useEffect(() => {
    if (phase === 'closed') {
      // Очищаем local state — каждое открытие = новый диалог
      setPrompt('');
      setTurns([]);
      setConvoId(null);
    }
  }, [phase]);

  async function submit(textInput?: string) {
    const text = (textInput ?? prompt).trim();
    if (!text || busy) return;

    // Optimistic: показываем user turn сразу
    setTurns((prev) => [...prev, { role: 'user', text }]);
    setPrompt('');
    setBusy(true);

    try {
      const resp = await fetch('/api/ai/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: convoId ?? undefined,
          prompt: text,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const err = data.error || `HTTP ${resp.status}`;
        setTurns((prev) => [...prev, { role: 'assistant', text: '', error: err }]);
        return;
      }
      setConvoId(data.conversation_id);
      setTurns((prev) => [...prev, { role: 'assistant', text: data.response || '' }]);
    } catch (err) {
      setTurns((prev) => [...prev, { role: 'assistant', text: '', error: String(err) }]);
    } finally {
      setBusy(false);
    }
  }

  // Defensive cleanup: blur focus при exit/close.
  useEffect(() => {
    if (phase === 'closing' || phase === 'closed') {
      // Если что-то сфокусировано — blur. Иначе фокус может оставаться
      // на unmount'ящемся input, что в некоторых браузерах ломает interactivity.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== document.body) {
        active.blur();
      }
    }
  }, [phase]);

  if (!shouldRender) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="AI Spotlight"
      className="fixed inset-0 z-spotlight"
      style={{
        // CRITICAL: pointer-events отключаются СРАЗУ при exit, не ждём конца анимации.
        // Это значит что во время closing (200ms) клики проходят сквозь весь overlay.
        pointerEvents: isExiting ? 'none' : 'auto',
      }}
    >
      {/* Backdrop — div, не button. Кликабельный через onClick.
          pointer-events: none во время isExiting (см. parent). */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className={cn(
          'absolute inset-0 cursor-default',
          isExiting ? 'animate-backdrop-out' : 'animate-backdrop-in',
        )}
        style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(12px) saturate(140%)' }}
      />

      {/* Card */}
      <div className="pointer-events-none relative flex h-full items-start justify-center px-6 pt-spotlight-offset">
        <div
          className={cn(
            'pointer-events-auto flex max-h-spotlight w-spotlight max-w-spotlight flex-col overflow-hidden',
            'rounded-2xl border border-glass-line bg-glass backdrop-blur-2xl backdrop-saturate-150',
            'shadow-spotlight',
            isExiting ? 'animate-drop-out' : 'animate-drop-in',
          )}
          // Card сама тоже не ловит pointer-events во время exit
          style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
        >
          {/* Header — input as form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-3"
          >
            <span className="grid size-8 shrink-0 place-items-center rounded-md bg-gradient-to-br from-ai-accent to-ai-emblem-end text-white shadow-ai-emblem">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            </span>
            <input
              ref={inputRef}
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={busy}
              placeholder={busy ? 'AI думает…' : 'Спроси AI или дай команду…'}
              className="flex-1 border-0 bg-transparent text-ai-input font-medium text-fg outline-none placeholder:text-fg-subtle disabled:opacity-60"
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрыть"
              className="grid size-7 shrink-0 place-items-center rounded-md text-fg-subtle transition-colors hover:bg-active hover:text-fg dark:hover:bg-white/10"
            >
              <X size={14} />
            </button>
            <kbd className="shrink-0 cursor-pointer rounded-xs bg-active px-2 py-0.5 font-mono text-2xs font-medium text-fg-subtle dark:bg-white/10">
              Esc
            </kbd>
          </form>

          {/* Body */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto p-4">
            {turns.length === 0 ? (
              // Suggestions — показываем когда диалог пуст
              <>
                <p className="mb-2 text-2xs font-semibold tracking-wider text-fg-subtle uppercase">
                  Подсказки
                </p>
                <div className="flex flex-col gap-1">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => void submit(s.label)}
                      disabled={busy}
                      className="flex items-center gap-3 rounded-md p-3 text-left transition-colors duration-fast hover:bg-muted disabled:opacity-50 dark:hover:bg-white/5"
                    >
                      <span className="grid size-7 shrink-0 place-items-center rounded-md bg-ai-accent/10 text-ai-icon dark:text-ai-icon-night">
                        {s.icon}
                      </span>
                      <span className="flex flex-1 flex-col">
                        <span className="text-ai-suggestion text-fg">{s.label}</span>
                        <span className="text-2xs text-fg-subtle">{s.meta}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <p className="mt-4 text-center text-2xs text-fg-subtle">
                  Sprint S1: чат работает без tool-calling. Доступ к каталогу и подключениям — позже.
                </p>
              </>
            ) : (
              // Conversation
              <div className="flex flex-col gap-3">
                {turns.map((turn, i) => (
                  <ChatTurnView key={i} turn={turn} />
                ))}
                {busy && (
                  <div className="flex items-center gap-2 px-3 py-2 text-xs text-fg-subtle">
                    <Loader2 size={12} className="animate-spin" />
                    AI обдумывает ответ…
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatTurnView({ turn }: { turn: ChatTurn }) {
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-chat-turn rounded-lg bg-gradient-to-br from-ai-accent to-ai-turn-end px-3 py-2 text-sm text-white shadow-ai-turn">
          {turn.text}
        </div>
      </div>
    );
  }
  // Assistant
  if (turn.error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-notice-error-line bg-notice-error-surface px-3 py-2 text-sm text-notice-error-text dark:border-notice-error-night-line dark:bg-notice-error-night/40 dark:text-notice-error-light-line">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="font-medium">Ошибка AI</p>
          <p className="text-xs opacity-90">{turn.error}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="max-w-chat-turn rounded-lg bg-muted px-3 py-2 text-sm whitespace-pre-wrap text-fg dark:bg-rail-control-soft">
        {turn.text}
      </div>
    </div>
  );
}
